import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api";

export default function BerryShopPanel({ mapId = 103, shopPlacementId = null, onClose, refreshEquippedItems }) {
  const [viewport, setViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [state, setState] = useState(null);
  const [activeTab, setActiveTab] = useState("shop");
  const [sellResult, setSellResult] = useState(null);

  const MAP_ID = Number(mapId) || 103;

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const layout = useMemo(() => {
    const isMobile = viewport.width <= 768;
    const panelWidth = isMobile
      ? Math.max(320, viewport.width - 32)
      : Math.max(520, Math.min(viewport.width - 64, 860));

    const panelMaxHeight = Math.max(420, Math.min(Math.floor(viewport.height * 0.9), 820));

    return {
      isMobile,
      panelWidth,
      panelMaxHeight,
    };
  }, [viewport.width, viewport.height]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const isMyIsland = Number(MAP_ID) === 103;
      const res = await api.get("/v1/island/berry-shop/state", {
        params: isMyIsland
          ? { map_id: MAP_ID, shop_placement_id: Number(shopPlacementId) || null, deduct_voltage: false }
          : {},
        withCredentials: true,
      });
      setState(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load berry shop.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [shopPlacementId]);

  const formatMoney = (n) => {
    const val = Number(n || 0);
    if (!Number.isFinite(val)) return String(n);
    return val.toLocaleString();
  };

  const formatNumber = (n) => {
    const val = Number(n);
    if (!Number.isFinite(val)) return String(n ?? "");
    return val.toLocaleString();
  };

  const renderIcon = (src, alt, size = 16) => (
    <img
      src={src}
      alt={alt}
      style={{ width: size, height: size, flexShrink: 0 }}
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = "/items/pokeball.png";
      }}
    />
  );

  const renderBalancePill = (iconSrc, label, value) => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        fontSize: 12,
        opacity: 0.95,
      }}
    >
      {renderIcon(iconSrc, label, 16)}
      <span style={{ fontWeight: 700 }}>{formatMoney(value)}</span>
      <span style={{ opacity: 0.85 }}>{label}</span>
    </div>
  );

  const handleBuy = async (itemId, quantity = 1) => {
    try {
      await api.post(
        "/v1/island/berry-shop/buy",
        { item_id: itemId, quantity, map_id: MAP_ID, shop_placement_id: Number(shopPlacementId) || null },
        { withCredentials: true }
      );
      if (refreshEquippedItems) await refreshEquippedItems();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to buy item.");
    }
  };

  const handleSellBerry = async (berryItemId, amount) => {
    try {
      const res = await api.post(
        "/v1/island/berry-shop/sell",
        { berry_item_id: berryItemId, amount, map_id: MAP_ID, shop_placement_id: Number(shopPlacementId) || null },
        { withCredentials: true }
      );
      if (res?.data?.success) {
        setSellResult(res.data);
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to sell berries.");
    }
  };

  const handleRepairRod = async (rodItemId) => {
  try {
    await api.post(
      "/v1/island/berry-shop/repair-rod",
      { rod_item_id: rodItemId, map_id: MAP_ID, shop_placement_id: Number(shopPlacementId) || null },
      { withCredentials: true }
    );
    if (refreshEquippedItems) await refreshEquippedItems();
    await load();
    // Additional call to ensure parent components update with latest durability bonuses
    if (refreshEquippedItems) refreshEquippedItems();
  } catch (e) {
    setError(e.response?.data?.detail || "Failed to repair rod.");
  }
};

  const COIN_ICON = "/items/coin.png";

  const modifiers = state?.modifiers || {};
  const sellBonusPct = Math.round((Number(modifiers.berry_sell_bonus || 0) || 0) * 100);
  const shopDiscountPct = Math.round((Number(modifiers.berry_shop_discount || 0) || 0) * 100);
  const repairDiscountPct = Math.round((Number(modifiers.rod_repair_discount || 0) || 0) * 100);

  return (
    <div
      style={{
        width: layout.panelWidth,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: layout.panelMaxHeight,
        background: "rgba(25, 25, 35, 0.95)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        borderRadius: 12,
        overflow: "hidden",
        color: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: "rgba(0,0,0,0.25)",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}>Berry Shop</div>
          <div style={{ opacity: 0.85, fontSize: 12, marginTop: 4 }}>
            Trade berries, buy supplies, and repair gear.
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.18)",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setActiveTab("shop")}
          style={{
            background: activeTab === "shop" ? "rgba(255,255,255,0.10)" : "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#fff",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Shop
        </button>
        <button
          onClick={() => setActiveTab("sell")}
          style={{
            background: activeTab === "sell" ? "rgba(255,255,255,0.10)" : "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#fff",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Berry Selling
        </button>
        <button
          onClick={() => setActiveTab("repairs")}
          style={{
            background: activeTab === "repairs" ? "rgba(255,255,255,0.10)" : "transparent",
            border: "1px solid rgba(255,255,255,0.14)",
            color: "#fff",
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          Repairs
        </button>
      </div>

      <div
        style={{
          padding: 14,
          overflowY: "auto",
          maxHeight: layout.panelMaxHeight - 110,
        }}
      >
        {sellResult && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 11000,
              padding: 16,
              boxSizing: "border-box",
            }}
            onClick={() => setSellResult(null)}
          >
            <div
              style={{
                width: "min(520px, calc(100vw - 32px))",
                background: "rgba(25, 25, 35, 0.98)",
                border: "1px solid rgba(255,255,255,0.12)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                borderRadius: 12,
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  padding: "12px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "rgba(0,0,0,0.25)",
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <div style={{ fontWeight: 800 }}>Sale Complete</div>
                <button
                  onClick={() => setSellResult(null)}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#fff",
                    borderRadius: 8,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 13 }}>Received</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderRadius: 12,
                    background: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    marginBottom: 12,
                  }}
                >
                  {renderIcon(COIN_ICON, "Coins", 28)}
                  <div style={{ fontSize: 13, opacity: 0.95 }}>
                    <span style={{ fontWeight: 800 }}>{formatNumber(sellResult?.received?.amount || 0)}</span> Coins
                  </div>
                </div>

                <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 13 }}>Sold</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: 10,
                    borderRadius: 12,
                    background: "rgba(0,0,0,0.28)",
                    border: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  {renderIcon(
                    sellResult?.sold?.image ? `/${sellResult.sold.image}` : "/items/pokeball.png",
                    sellResult?.sold?.name || "Berry",
                    28
                  )}
                  <div style={{ fontSize: 13, opacity: 0.95 }}>
                    <div>
                      <span style={{ fontWeight: 800 }}>{formatNumber(sellResult?.sold?.amount || 0)}</span>{" "}
                      {sellResult?.sold?.name || "Berries"}
                    </div>
                    <div style={{ opacity: 0.85, marginTop: 4 }}>
                      {sellResult?.sold?.bonus_pct ? (
                        <>
                          Unit value: {formatNumber(sellResult?.sold?.unit_value || 0)} (base {formatNumber(sellResult?.sold?.base_unit_value || 0)}, +{sellResult.sold.bonus_pct}%)
                        </>
                      ) : (
                        <>Unit value: {formatNumber(sellResult?.sold?.unit_value || 0)}</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(248,113,113,0.18)",
              border: "1px solid rgba(248,113,113,0.35)",
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ opacity: 0.9, fontSize: 13 }}>Loading...</div>
        ) : (
          <>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Balances</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              {renderBalancePill(COIN_ICON, "Coins", state?.balances?.coins || 0)}
              {(state?.berries || []).map((b) =>
                renderBalancePill(
                  b?.image ? `/${b.image}` : "/items/pokeball.png",
                  b?.name || "Berry",
                  b?.owned_amount || 0
                )
              )}
            </div>

            {(sellBonusPct > 0 || shopDiscountPct > 0 || repairDiscountPct > 0) && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "10px 12px",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  fontSize: 12,
                  opacity: 0.95,
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Active Farming Bonuses</div>
                {sellBonusPct > 0 && <div>Berry selling: +{sellBonusPct}%</div>}
                {repairDiscountPct > 0 && <div>Rod repairs: -{repairDiscountPct}% cost</div>}
                {shopDiscountPct > 0 && <div>Berry Shop: -{shopDiscountPct}% costs</div>}
              </div>
            )}

            {activeTab === "shop" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Shop</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(state?.shop_items || []).map((it) => {
                    const owned = Number(it.owned_amount || 0) > 0;
                    const disabledReason = it.disabled_reason || "";
                    const canBuy = !!it.can_buy;

                    return (
                      <div
                        key={it.item_id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        <img
                          src={it?.image ? `/${it.image}` : "/items/pokeball.png"}
                          alt={it?.name || "Item"}
                          style={{ width: 44, height: 44, flexShrink: 0 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/items/pokeball.png";
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 14 }}>
                            {it?.name || `Item ${it.item_id}`}
                          </div>
                          {it?.description && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, lineHeight: 1.35 }}>
                              {it.description}
                            </div>
                          )}
                          {Number(it?.uses_remaining || 0) > 0 && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6 }}>
                              Uses remaining: {formatNumber(it.uses_remaining)}
                            </div>
                          )}
                          <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                            {it.cost_type === "coins" ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span>Cost:</span>
                                {renderIcon(COIN_ICON, "Coins", 16)}
                                <span>
                                  {Number(it.base_cost_amount) > Number(it.cost_amount) ? (
                                    <>
                                      <span style={{ textDecoration: "line-through", opacity: 0.75 }}>
                                        {formatNumber(it.base_cost_amount)}
                                      </span>{" "}
                                      <span style={{ fontWeight: 800 }}>{formatNumber(it.cost_amount)}</span>
                                      {it.discount_pct ? <span style={{ opacity: 0.85 }}> ({it.discount_pct}% off)</span> : null}
                                    </>
                                  ) : (
                                    <>{formatNumber(it.cost_amount)}</>
                                  )}{" "}
                                  Coins
                                </span>
                              </span>
                            ) : (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span>Cost:</span>
                                {renderIcon(it?.cost_item?.image ? `/${it.cost_item.image}` : "/items/pokeball.png", it?.cost_item?.name || "Item", 16)}
                                <span>
                                  {Number(it.base_cost_amount) > Number(it.cost_amount) ? (
                                    <>
                                      <span style={{ textDecoration: "line-through", opacity: 0.75 }}>
                                        {formatNumber(it.base_cost_amount)}
                                      </span>{" "}
                                      <span style={{ fontWeight: 800 }}>{formatNumber(it.cost_amount)}</span>
                                      {it.discount_pct ? <span style={{ opacity: 0.85 }}> ({it.discount_pct}% off)</span> : null}
                                    </>
                                  ) : (
                                    <>{formatNumber(it.cost_amount)}</>
                                  )}
                                  × {it?.cost_item?.name || it.cost_item_id}
                                </span>
                              </span>
                            )}
                          </div>
                          {disabledReason && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, color: "#fca5a5" }}>
                              {disabledReason}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                          <button
                            onClick={() => handleBuy(it.item_id, 1)}
                            disabled={!canBuy}
                            style={{
                              background: canBuy ? "rgba(34,197,94,0.80)" : "rgba(255,255,255,0.10)",
                              border: "1px solid rgba(255,255,255,0.14)",
                              color: "#fff",
                              borderRadius: 10,
                              padding: "8px 12px",
                              cursor: canBuy ? "pointer" : "not-allowed",
                              minWidth: 92,
                            }}
                          >
                            Buy
                          </button>
                          <div style={{ opacity: 0.85, fontSize: 12 }}>Owned: {formatNumber(it.owned_amount || 0)}</div>
                          {owned && it.cost_type === "coins" && (
                            <div style={{ opacity: 0.85, fontSize: 12 }}>Durability: {formatNumber(it.uses_remaining || 0)}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {activeTab === "sell" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Sell Berries</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(state?.sell_prices || []).map((b) => {
                    const owned = Number(b.owned_amount || 0);
                    const canSell = owned > 0;

                    const unit = Number(b.coin_value || 0);
                    const baseUnit = Number(b.base_coin_value || b.coin_value || 0);
                    const allValue = owned * unit;

                    return (
                      <div
                        key={b.item_id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        <img
                          src={b?.image ? `/${b.image}` : "/items/pokeball.png"}
                          alt={b?.name || "Berry"}
                          style={{ width: 44, height: 44, flexShrink: 0 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/items/pokeball.png";
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 14 }}>{b?.name || b.item_id}</div>
                          <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6 }}>
                            Owned: {formatNumber(owned)}
                          </div>
                          <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span>Value:</span>
                              {renderIcon(COIN_ICON, "Coins", 16)}
                              <span>
                                {baseUnit !== unit ? (
                                  <>
                                    <span style={{ textDecoration: "line-through", opacity: 0.75 }}>{formatNumber(baseUnit)}</span>{" "}
                                    <span style={{ fontWeight: 800 }}>{formatNumber(unit)}</span>
                                    {b.bonus_pct ? <span style={{ opacity: 0.85 }}> (+{b.bonus_pct}%)</span> : null}
                                  </>
                                ) : (
                                  <>
                                    {formatNumber(unit)}
                                    {b.bonus_pct ? <span style={{ opacity: 0.85 }}> (+{b.bonus_pct}%)</span> : null}
                                  </>
                                )}{" "}
                                coins each
                              </span>
                              <span style={{ opacity: 0.6 }}>|</span>
                              <span>Sell all:</span>
                              <span>{formatNumber(allValue)} coins</span>
                            </span>
                          </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                          <button
                            onClick={() => handleSellBerry(b.item_id, owned)}
                            disabled={!canSell}
                            style={{
                              background: canSell ? "rgba(59,130,246,0.85)" : "rgba(255,255,255,0.10)",
                              border: "1px solid rgba(255,255,255,0.14)",
                              color: "#fff",
                              borderRadius: 10,
                              padding: "8px 12px",
                              cursor: canSell ? "pointer" : "not-allowed",
                              minWidth: 120,
                            }}
                          >
                            Sell All
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {activeTab === "repairs" && (
              <>
                <div style={{ fontWeight: 800, marginBottom: 8 }}>Repairs</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {(state?.repairs || []).map((r) => {
                    const owned = !!r.owned;
                    const durability = Number(r.durability || 0);
                    const maxDurability = Number(r.max_durability || 0);
                    const needsRepair = owned && durability < maxDurability;
                    const canRepair = !!r.can_repair;

                    return (
                      <div
                        key={r.rod_item_id}
                        style={{
                          display: "flex",
                          gap: 12,
                          alignItems: "center",
                          padding: 12,
                          borderRadius: 12,
                          background: "rgba(0,0,0,0.28)",
                          border: "1px solid rgba(255,255,255,0.10)",
                        }}
                      >
                        <img
                          src={r?.image ? `/${r.image}` : "/items/pokeball.png"}
                          alt={r?.name || "Rod"}
                          style={{ width: 44, height: 44, flexShrink: 0 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/items/pokeball.png";
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 14 }}>{r?.name || r.rod_item_id}</div>
                          {r?.description && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, lineHeight: 1.35 }}>
                              {r.description}
                            </div>
                          )}
                          {owned && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6 }}>
                              Durability: {formatNumber(durability)}/{formatNumber(maxDurability)}
                            </div>
                          )}
                          <div style={{ opacity: 0.88, fontSize: 12, marginTop: 6 }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span>Repair Cost:</span>
                              {renderIcon(r?.cost_item?.image ? `/${r.cost_item.image}` : "/items/pokeball.png", r?.cost_item?.name || "Berry", 16)}
                              <span>
                                {Number(r.base_cost_amount) > Number(r.cost_amount) ? (
                                  <>
                                    <span style={{ textDecoration: "line-through", opacity: 0.75 }}>
                                      {formatNumber(r.base_cost_amount)}
                                    </span>{" "}
                                    <span style={{ fontWeight: 800 }}>{formatNumber(r.cost_amount)}</span>
                                    {r.discount_pct ? <span style={{ opacity: 0.85 }}> ({r.discount_pct}% off)</span> : null}
                                  </>
                                ) : (
                                  <>{formatNumber(r.cost_amount)}</>
                                )}
                                × {r?.cost_item?.name || r.cost_item_id}
                              </span>
                            </span>
                          </div>
                          {!!r.disabled_reason && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6, color: "#fca5a5" }}>
                              {r.disabled_reason}
                            </div>
                          )}
                          {owned && !needsRepair && (
                            <div style={{ opacity: 0.9, fontSize: 12, marginTop: 6 }}>No repairs needed.</div>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                          <button
                            onClick={() => handleRepairRod(r.rod_item_id)}
                            disabled={!canRepair}
                            style={{
                              background: canRepair ? "rgba(168,85,247,0.78)" : "rgba(255,255,255,0.10)",
                              border: "1px solid rgba(255,255,255,0.14)",
                              color: "#fff",
                              borderRadius: 10,
                              padding: "8px 12px",
                              cursor: canRepair ? "pointer" : "not-allowed",
                              minWidth: 92,
                            }}
                          >
                            Repair
                          </button>
                          <div style={{ opacity: 0.85, fontSize: 12 }}>{owned ? "Owned" : "Not owned"}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
